import { ChevronDown, Icon } from "@norican/icons";
import { Badge, Button, cn, Menu, NavigationMenu } from "@norican/react";
import { Fragment } from "react";
import { Link, useLocation } from "react-router";

import { useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { activeNavItem, isNavItemActive, primaryNavItemsFor } from "./nav-items";
import { type ItAdminNav, useItAdminNav } from "./use-it-admin-nav";

export interface PrimaryNavProps {
  className?: string;
}

/**
 * The horizontal navigation beside the brand (6 Oct, decision 35).
 *
 * Everyone's header is the brand, My tickets, language and the account menu,
 * all rendered by `AppHeader`. What this adds is the staff side: one
 * "IT Admin ▾" menu with every tool the reader may use, grouped, plus "New
 * starters" for HR while the access front door is off.
 *
 * Plain links are `NavigationMenu.Link`s rendered *as* React Router `<Link>`s
 * (one real `<a href>`, middle-clickable). "IT Admin" is a design-system
 * `Menu`: roving focus, arrow keys, Escape and typeahead come with it, and
 * every entry is still a real link.
 */
export function PrimaryNav({ className }: PrimaryNavProps) {
  const { t } = useT();
  const { pathname } = useLocation();
  const { data: me } = useMe();
  const items = primaryNavItemsFor(me);
  const itAdmin = useItAdminNav();

  // Nothing to show (every regular user): no <nav> rather than an empty
  // landmark the screen reader still announces.
  if (items.length === 0 && itAdmin.groups.length === 0) {
    return null;
  }

  return (
    <NavigationMenu.Root className={className} aria-label={t("nav.label")}>
      <NavigationMenu.List className="flex items-center gap-1">
        {items.map((item) => {
          const active = isNavItemActive(item, pathname);
          return (
            <NavigationMenu.Item key={item.id}>
              <NavigationMenu.Link
                active={active}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
                  active ? "text-accent-fg-subtle bg-accent-subtle" : "text-fg-muted hover:text-fg",
                )}
                render={<Link to={item.to} />}
              >
                <Icon icon={item.icon} size="sm" />
                {t(item.labelKey)}
              </NavigationMenu.Link>
            </NavigationMenu.Item>
          );
        })}
        {itAdmin.groups.length > 0 ? (
          <NavigationMenu.Item>
            <ItAdminMenu nav={itAdmin} />
          </NavigationMenu.Item>
        ) : null}
      </NavigationMenu.List>
    </NavigationMenu.Root>
  );
}

/** The "IT Admin ▾" dropdown: grouped, permitted items only, with the badge. */
function ItAdminMenu({ nav }: { nav: ItAdminNav }) {
  const { t } = useT();
  const { pathname, search } = useLocation();
  const { groups, items, pending } = nav;
  const current = activeNavItem(items, pathname, search);

  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            aria-label={
              pending > 0 ? t("itAdminMenu.triggerPending", { n: pending }) : t("nav.itAdmin")
            }
            endIcon={<Icon icon={ChevronDown} size="sm" />}
            className={cn(current !== null && "text-accent-fg-subtle bg-accent-subtle")}
          />
        }
      >
        <span className="flex items-center gap-2">
          {t("nav.itAdmin")}
          {pending > 0 ? (
            <Badge tone="accent" variant="solid" size="xs" aria-hidden="true">
              {pending}
            </Badge>
          ) : null}
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="start">
          <Menu.Popup width="content">
            {groups.map((group, index) => (
              <Fragment key={group.id}>
                {/* A clear rule and a brand-blue heading per group (7 Oct): the
                    design system's muted label and hairline read as one list. */}
                {index > 0 ? <Menu.Separator className="bg-border-strong my-2" /> : null}
                <Menu.Group>
                  <Menu.GroupLabel className="text-accent pt-1.5 pb-1 font-bold tracking-widest">
                    {t(group.labelKey)}
                  </Menu.GroupLabel>
                  {group.items.map((item) => {
                    const active = current?.id === item.id;
                    return (
                      <Menu.Item
                        key={item.id}
                        render={<Link to={item.to} />}
                        aria-current={active ? "page" : undefined}
                        className={cn(active && "text-accent-fg-subtle")}
                      >
                        <span className="flex w-full items-center gap-2">
                          <Icon icon={item.icon} size="sm" />
                          <span className="flex-1">{t(item.labelKey)}</span>
                          {item.badge === "access" && pending > 0 ? (
                            <Badge tone="accent" variant="solid" size="xs">
                              {pending}
                            </Badge>
                          ) : null}
                        </span>
                      </Menu.Item>
                    );
                  })}
                </Menu.Group>
              </Fragment>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
